import React from "react";
import { Area, ComposedChart, ResponsiveContainer, Tooltip } from "recharts";
import { count, money } from "../format";
import { Delta, InfoTip, SeriesTooltip, Skeleton } from "./primitives";

const SPARK_HEIGHT = 56;

/* Series that count things rather than measure money. */
const COUNT_KEYS = new Set(["orders", "sheets"]);

const Sparkline = ({ series, seriesKey, label, color }) => {
  if (!series || series.length < 2) return <div style={{ height: SPARK_HEIGHT }} />;
  const format = COUNT_KEYS.has(seriesKey) ? count : money;

  return (
    <ResponsiveContainer width="100%" height={SPARK_HEIGHT}>
      <ComposedChart data={series} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id={`spark-${seriesKey}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.28} />
            <stop offset="100%" stopColor={color} stopOpacity={0.02} />
          </linearGradient>
        </defs>
        <Tooltip cursor={{ stroke: color, strokeWidth: 1 }} content={<SeriesTooltip format={format} />} />
        <Area
          type="monotone"
          dataKey={seriesKey}
          name={label}
          stroke={color}
          strokeWidth={2}
          fill={`url(#spark-${seriesKey})`}
          dot={false}
          activeDot={{ r: 4, strokeWidth: 2, stroke: "var(--dash-surface)" }}
          isAnimationActive={false}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
};

/** Headline figure with period-over-period change, a sparkline and an optional (i) tip. */
export const KpiCard = ({ label, value, delta, invert, caption, color, series, seriesKey, info, loading }) => (
  <section className="dash-card" aria-label={label}>
    <div className="dash-kpi">
      <p className="dash-kpi-label">
        <span className="dash-kpi-dot" style={{ background: color }} aria-hidden="true" />
        {label}
        {info && <InfoTip label={`How ${label.toLowerCase()} is calculated`}>{info}</InfoTip>}
      </p>
      {loading ? (
        <>
          <Skeleton height={28} width="65%" className="mg-t-10" />
          <Skeleton height={12} width="40%" className="mg-t-8" />
        </>
      ) : (
        <>
          <p className="dash-kpi-value">{value}</p>
          <p className="dash-kpi-foot">
            <Delta change={delta} invert={invert} />
            <span>{caption}</span>
          </p>
        </>
      )}
    </div>
    <div className="dash-kpi-spark" aria-hidden="true">
      {loading ? <div style={{ height: SPARK_HEIGHT }} /> : <Sparkline series={series} seriesKey={seriesKey} label={label} color={color} />}
    </div>
  </section>
);

/** Secondary figure: a value, an optional change, a one-line explanation and an optional (i) tip. */
export const StatCard = ({ label, hint, value, delta, caption, info, loading }) => (
  <section className="dash-card" aria-label={label}>
    <div className="dash-stat">
      <p className="dash-stat-label">
        {label}
        {info && <InfoTip label={`How ${label.toLowerCase()} is calculated`}>{info}</InfoTip>}
        {hint && <span className="dash-stat-hint">{hint}</span>}
      </p>
      {loading ? (
        <>
          <Skeleton height={26} width="55%" className="mg-t-10" />
          <Skeleton height={12} width="70%" className="mg-t-8" />
        </>
      ) : (
        <>
          <p className="dash-kpi-value">{value}</p>
          <p className="dash-kpi-foot">
            {delta !== undefined && <Delta change={delta} />}
            <span>{caption}</span>
          </p>
        </>
      )}
    </div>
  </section>
);
